<template>
  <v-app>
    <v-dialog v-model="showLoginError" width="450"
      ><LoginErrorPopup v-if="showLoginError"
    /></v-dialog>
    <v-dialog v-model="showMagicLinkWarningModal" persistent width="450"
      ><MagicLinkExpirationPopup v-if="showMagicLinkWarningModal"
    /></v-dialog>
    <!-- ADO #189684: the signed-in user's client does not own this host. The
         popup carries its own Modal, which is persistent and has no close
         affordance, so the shell behind it stays unusable until they pick one
         of the two ways out. -->
    <WhitelabelRedirectPopup
      :value="hostDenied"
      :redirect-domain="hostRedirectDomain"
      dismiss-label="Sign out"
      @decline="logoutFromDeniedHost"
    />

    <!-- failedWhitelabel alone must wait for featureFlagsResolved so this never
         mounts (and picks a gradient) ahead of the feature-flags white screen
         below — same "flags decide first" rule, no exception for errors. -->
    <AppErrorScreen
      v-if="
        failedLogin ||
        failedPermissions ||
        showClientBlacklist ||
        (failedWhitelabel && featureFlagsResolved)
      "
      :blacklisted="showClientBlacklist"
    />
    <!-- Statically hosted Marqeta pages — do not wait on whitelabel config. -->
    <div class="fill-height" v-else-if="showCardActivationSuccess">
      <MarqetaCardActivationSuccess class="fill-height"></MarqetaCardActivationSuccess>
    </div>
    <div class="fill-height" v-else-if="showCardPINSuccess">
      <MarqetaCardPINSuccess class="fill-height"></MarqetaCardPINSuccess>
    </div>
    <div class="fill-height" v-else-if="showHostedVirtualCard">
      <MarqetaVirtualCardHosted class="fill-height"></MarqetaVirtualCardHosted>
    </div>
    <div class="fill-height" v-else-if="showHostedActivateCard">
      <MarqetaActivateCardHosted class="fill-height"></MarqetaActivateCardHosted>
    </div>
    <div class="fill-height" v-else-if="showHostedCardPIN">
      <MarqetaCreatePINHosted class="fill-height"></MarqetaCreatePINHosted>
    </div>
    <!-- Blank white screen while feature flags are unresolved — flags decide first.
         Whistle vs PerformX isn't known until flags resolve, so nothing brand-specific
         can render. Whitelabel being slow does not blank the screen — it falls through
         to the loading UI below once flags are resolved.
         The feature-flag connection quietly retries for up to 5s (a rejected
         attempt may just be a slow/throttled connection, not dead); only once
         that full budget runs out does it default to Whistle.
         For hard failure recovery: featureFlagsResolved may become true with
         featureFlagsFailed=true; the gate below still guards brand-dependent UI. -->
    <div v-else-if="!featureFlagsResolved" class="fill-height">
      <span class="visually-hidden" role="status" aria-live="polite">Loading</span>
    </div>
    <!-- Magic link has its own loading UI, but it stays behind the feature-flags
         white screen. LoginMagicLink treats an unresolved feature-performx-logo
         (default false) as the Whistle gradient, which flashes before PerformX.
         Once flags resolve and whitelabelReady, it wins over App loading so we
         don't remount AppLoadingScreen across Tier 1→2. -->
    <div class="fill-height" v-else-if="showMagicLinkLogin">
      <LoginMagicLink class="fill-height"></LoginMagicLink>
    </div>
    <!-- Once flags are resolved, show the loading screen even if whitelabel is
         still pending. The gradient follows feature-performx-logo immediately;
         whitelabel config does not choose it. -->
    <AppLoadingScreen v-else-if="featureFlagsResolved && loading" :show="show" />
    <div class="fill-height" v-else-if="showLogin">
      <Login class="fill-height"></Login>
    </div>
    <div
      class="fill-height"
      v-else-if="$route.name == 'user-signup' || $route.name == 'user-signup-static'"
    >
      <Activation class="fill-height"></Activation>
    </div>
    <!-- Otherwise, user is considered logged in -->
    <div class="fill-height authenticated-shell" v-else>
      <!-- If the user profile has loaded, then show main content -->
      <div class="fill-height">
        <!-- Show payments version of activation based on computed property -->
        <div v-if="showPaymentsActivation" class="fill-height">
          <PaymentsActivation class="fill-height"></PaymentsActivation>
        </div>
        <!-- If this is the user's first time, activation screen -->
        <div v-else-if="userProfile && userProfile.firstLogin" class="fill-height">
          <Activation class="fill-height"></Activation>
        </div>
        <!-- Otherwise, show main screen -->
        <div v-else class="fill-height">
          <navBar
            v-if="displayNavigationBar"
            @wallet-menu="walletToggler"
            @toggle-profile="toggleProfile"
            @toggle-nav="toggleMobileNav"
          ></navBar>

          <div id="app" class="full-height full-width">
            <!-- Left nav only visible on non-mobile -->
            <v-navigation-drawer
              v-if="!$vuetify.breakpoint.sm && !$vuetify.breakpoint.xs && displayNavigationDrawer"
              app
              permanent
              clipped
              expand-on-hover
              update:mini-variant="expanded"
              class="hidden-sm-and-down"
            >
              <navDrawer @show-mobile="showMobileModal = true"></navDrawer>
            </v-navigation-drawer>
            <!-- Nav drawer only visible on mobile - toggled by the menu button that appears on mobile -->
            <v-navigation-drawer
              v-model="showMobileNavDrawer"
              temporary
              fixed
              class="d-sm-flex d-md-none"
            >
              <navDrawer @show-mobile="showMobileModal = true"></navDrawer>
            </v-navigation-drawer>
            <!-- If the user is on mobile, show the popup with links -->
            <v-dialog v-model="showMobileModal" width="450"
              ><AppStorePopup @close="closeAppStorePopup"
            /></v-dialog>
            <v-main
              class="full-height"
              :style="
                !$vuetify.breakpoint.sm && !$vuetify.breakpoint.xs && displayNavigationDrawer
                  ? 'padding-left: 55px;'
                  : ''
              "
            >
              <div class="page-slot">
                <help-widget v-bind="help"></help-widget>
                <AppNotificationBanner v-if="appNotification" :banner="appNotification" />
                <div
                  class="page-slot__body"
                  :class="{
                    'main-content-top': displayNavigationDrawer,
                    'main-content-sides': displayNavigationDrawer && !isMobile,
                    'page-slot__body--below-banner': !!appNotification,
                  }"
                >
                  <router-view />
                </div>
              </div>
            </v-main>
          </div>
        </div>

        <!-- Begin wallet modal -->
        <WalletDrawer />

        <!-- End User Profile Modal -->
        <UserProfileDrawer v-model="profileMenu" />
      </div>
    </div>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
      multi-line
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" icon v-bind="attrs" @click="hideSnackbar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import navDrawer from "@/components/navDrawer.vue";
import navBar from "@/components/navBar.vue";
import AppStorePopup from "@/components/AppStorePopup.vue";
import LoginErrorPopup from "@/components/LoginErrorPopup.vue";
import MagicLinkExpirationPopup from "@/components/MagicLinkExpirationPopup.vue";
import WhitelabelRedirectPopup from "@/components/app/WhitelabelRedirectPopup.vue";
import Activation from "@/views/ActivationV3.vue";
import PaymentsActivation from "@/views/ActivationV3Payments.vue";
import MarqetaCardActivationSuccess from "@/views/MarqetaCardActivationSuccess.vue";
import MarqetaCardPINSuccess from "@/views/MarqetaCardPINSuccess.vue";
import MarqetaVirtualCardHosted from "@/views/MarqetaVirtualCardHosted.vue";
import MarqetaActivateCardHosted from "@/views/MarqetaActivateCardHosted.vue";
import MarqetaCreatePINHosted from "@/views/MarqetaCreatePINHosted";
import Login from "@/views/Login.vue";
import LoginMagicLink from "@/views/LoginMagicLink.vue";

import { mapState } from "vuex";
import UserProfileDrawer from "@/components/app/UserProfileDrawer.vue";
import WalletDrawer from "@/components/wallet/WalletDrawer.vue";
import HelpWidget from "./components/main/HelpWidget.vue";
import AppErrorScreen from "@/components/app/AppErrorScreen.vue";
import AppLoadingScreen from "@/components/app/AppLoadingScreen.vue";

// Imports Quill styling so any html text with custom classes gets rendered properly
import "quill/dist/quill.core.css";

import "@/css/vuetify-fix.css";

import FeatureFlagService from "@/services/featureflags/FeatureFlagService";
import AppNotificationBanner from "@/components/app/AppNotificationBanner.vue";

export default {
  components: {
    AppNotificationBanner,
    HelpWidget,
    navDrawer,
    navBar,
    AppStorePopup,
    LoginErrorPopup,
    MagicLinkExpirationPopup,
    WhitelabelRedirectPopup,
    Activation,
    PaymentsActivation,
    Login,
    LoginMagicLink,
    AppErrorScreen,
    AppLoadingScreen,
    MarqetaCardActivationSuccess,
    MarqetaCardPINSuccess,
    MarqetaVirtualCardHosted,
    MarqetaActivateCardHosted,
    MarqetaCreatePINHosted,
    WalletDrawer,
    UserProfileDrawer,
  },

  async created() {
    // Always hold the loading flag through Tier 1 — including session-restore /
    // cached-profile paths where $auth may already report authenticated.
    this.loading = true;
    // Close the gate synchronously before the await so a persisted
    // whitelabelReady=true from a prior session cannot unlock the shell
    // while the request is still in flight.
    this.$store.commit("SET_WHITELABEL_READY", false);
    const whitelabelPromise = this.$store.dispatch("fetchWhitelabelConfig");

    // Bug #189562 (Cycle 7, PR review fix): the 5s timer must not outlive its
    // usefulness. It's scheduled once, here, for the life of the app — if the
    // real connection succeeds quickly, clearing it avoids a stale fire later.
    // Worse: if nothing is cleared and the user LOGOUTs before the stale timer
    // fires, that logout has already reset featureFlagsResolved to false, and
    // the leftover timer would force it back to true against post-logout
    // default flags — the exact stale-latch bug Cycle 5 fixed, via a new
    // path. loggedOutSinceScheduled blocks that regardless of the timer.
    let flagsGateForced = false;
    let loggedOutSinceScheduled = false;
    let timeoutId = null;
    // Single give-up point: the whole 5s budget below decides "not coming
    // back," not individual failed attempts — a rejected attempt might just
    // be a slow/throttled connection, so retries keep trying quietly. Once
    // this fires, it is final: stop retrying and stop listening entirely, so
    // a decision already shown to the user is never corrected later (that
    // correction is itself a flash).
    const commitFeatureFlagsGiveUp = async () => {
      if (flagsGateForced || loggedOutSinceScheduled) return;
      flagsGateForced = true;
      await FeatureFlagService.kill();
      this.$store.commit("SET_FEATURE_FLAGS_FAILED", true);
      await this.$store.dispatch("hardFailureRecovery");
    };
    const stopWatchingForLogout = this.$store.subscribe((mutation) => {
      if (mutation.type === "LOGOUT") loggedOutSinceScheduled = true;
    });

    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, 5000);
    }).then(commitFeatureFlagsGiveUp);

    // The flagd SDK has no built-in retry of a resolveAll failure that
    // happens after its event stream already connected (see
    // node_modules/@openfeature/flagd-web-provider — that request's promise
    // just hangs forever with no catch), so we detect that specific failure
    // via the "flagd-resolve-all-failed" signal (dispatched by
    // public/index.html's early unhandledrejection handler) and retry it
    // ourselves, quietly, until the 5s budget above runs out.
    const attemptFeatureFlagsConnect = () => {
      let onResolveAllFailed;
      return new Promise((resolve, reject) => {
        onResolveAllFailed = () => reject(new Error("flagd resolveAll failed"));
        window.addEventListener("flagd-resolve-all-failed", onResolveAllFailed);

        FeatureFlagService.initialize({
          onConfigurationChanged: () => {
            this.$store.dispatch("setFeatureFlags");
          },
          onReady: () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            this.$store.dispatch("setFeatureFlags");
            resolve();
          },
          onError: (e) => reject(e),
        }).catch(reject);
      }).finally(() => window.removeEventListener("flagd-resolve-all-failed", onResolveAllFailed));
    };

    const featureFlagsPromise = (async () => {
      try {
        await Promise.race([
          (async () => {
            for (;;) {
              if (flagsGateForced) return; // already gave up — stop retrying entirely
              try {
                await attemptFeatureFlagsConnect();
                return;
              } catch (e) {
                if (flagsGateForced) return; // gave up mid-attempt — stop retrying entirely
                await FeatureFlagService.kill();
                await new Promise((r) => setTimeout(r, 300));
              }
            }
          })(),
          timeoutPromise,
        ]);
      } finally {
        stopWatchingForLogout();
      }
    })();

    await Promise.all([whitelabelPromise, featureFlagsPromise]);

    // Belt-and-suspenders call removed: setFeatureFlags now commits
    // dashboardConfig after setContext resolves, eliminating the race.

    //When loading, show second message after 3 seconds
    setTimeout(() => (this.show = true), 3000);

    //If Auth0 is still loading after 15 seconds then we show a popup
    setTimeout(() => {
      if (
        !this.showLogin &&
        !this.showMagicLinkLogin &&
        (!this.$auth || this.$auth.loading || this.$auth.errorType) &&
        !this.failedLogin &&
        !this.showClientBlacklist &&
        !this.magicLinkToken
      )
        this.showLoginError = true;
      else console.log("Not showing login error");
    }, 15000);
    // Checks if we're on a mobile device and if the user already closed the mobile dialog
    if (
      !this.postItArray.includes("mobile-1") &&
      this.$isMobile() &&
      this.userProfile &&
      this.userProfile.Client &&
      this.userProfile.Client.displayMobilePopup &&
      this.$route.name != "walletappclip" &&
      this.$route.name != "forms"
    ) {
      this.showMobileModal = true;
    }

    // If the page loads and the wallet is open, refresh balance
    if (this.$store.state.displayWalletMenu == true) {
      this.$store.dispatch("setDisplayWalletMenu", false);
    }
  },
  async beforeUnmount() {
    // this.unwatch();
    await FeatureFlagService.kill();
  },
  data() {
    return {
      help: {
        direction: "top",
        fab: false,
        fling: false,
        hover: false,
        tabs: null,
        top: false,
        right: true,
        bottom: true,
        left: false,
        transition: "slide-y-reverse-transition",
      },
      loading: false,
      show: false,
      showLoginError: false,
      profileMenu: false,
      showMobileNavDrawer: false,
      // loadingBalance: false,
      routesWithoutNavBar: [
        "login",
        "spirit-login",
        "generic-3pa-login",
        "emailunsubscribe",
        "surveyPublic",
        "forms",
        "walletactivationsuccess",
        "walletpinsuccess",
        "marqetavirtualcardhosted",
        "marqetaactivatecardhosted",
        "marqetacreatepinhosted",
        "setup",
        "slacksetup",
        "user-signup",
        "user-signup-static",
        "login-error",
        "procore",
        "peoplefilewizard",
      ],
    };
  },
  mounted() {},
  methods: {
    /**
     * ADO #189684: "No" on the wrong-site modal. Clears the session and lands
     * them on this site's login page, where the email gate stops them again
     * rather than letting them straight back in.
     */
    logoutFromDeniedHost() {
      this.$auth.logout({ returnTo: window.location.origin });
    },
    toggleProfile() {
      this.profileMenu = !this.profileMenu;
    },
    closeAppStorePopup() {
      this.showMobileModal = false;
      if (!this.postItArray.find((x) => x == "mobile-1"))
        this.$store.dispatch("setPostItArray", "mobile-1");
    },
    walletToggler: function () {
      console.log("toggler called in app.vue");
      this.$store.dispatch("setDisplayWalletMenu", true);
      this.$store.dispatch("setWalletMenuScreen", "main");
      this.$store.dispatch("setSourceWalletPage", null);
    },
    toggleMobileNav() {
      console.log("Success");
      // This is called only for the mobile nav drawer
      this.showMobileNavDrawer = true;
    },
    // Function to dispatch the action to hide the snackbar
    hideSnackbar() {
      this.$store.dispatch("hideSnackbar");
    },
  },
  computed: {
    /**
     * The app-level announcement slot. Mounted in the shell above `router-view`
     * so any view can carry an announcement, then narrowed by whatever routes
     * that specific announcement is scoped to, and by its visibility window.
     *
     * Scope lives in the payload (`AppNotification.routes` / `appearsOnRoute`),
     * not here — moved out of a hardcoded route check on 2026-08-28 (ADO
     * 189199) after the same flag started carrying a platform-wide rebrand
     * announcement that the old `if (route !== "programs")` check silently
     * suppressed on every route except /programs. Mirrors mobile's
     * `AppNotification.appearsOnTab` (ADO 189200, `router_no_shell.dart`).
     *
     * An announcement with no `routes` (the rebrand banner) appears
     * everywhere except routes named in `excludedRoutes`; one that declares
     * `routes: ["programs"]` (the retirement banner) stays scoped exactly as
     * it was. An exclusion wins when a route is in both lists.
     *
     * The window check (`isVisibleAt`) is new the same day (185519 AC7 /
     * ADO 189199 AC1-AC4). `startDate`/`endDate` were already present, unread,
     * in the live GitOps payload — a config field with no consumer — so this
     * closes that gap rather than adding one. `new Date()` is read once per
     * evaluation of this computed (route change or store update), not on a
     * timer; an already-mounted banner will not vanish mid-session purely
     * because the end date passed with no other reactive trigger. Mirrors
     * mobile's `AppNotification.isVisibleAt` (ADO 189200) exactly.
     */
    appNotification() {
      const banner = this.$store.getters.appNotification;
      if (!banner) return null;
      if (!banner.appearsOnRoute(this.$route.name)) return null;
      return banner.isVisibleAt(new Date()) ? banner : null;
    },
    ...mapState([
      "snackbar",
      "userProfile",
      "failedLogin",
      "failedWhitelabel",
      "failedPermissions",
      "whitelabelReady",
      "featureFlagsResolved",
      "featureFlagsFailed",
      "postItArray",
      "displayMobilePopup",
      "magicLinkToken",
      "magicLinkTokenExpirationWarning",
    ]),
    showMobileModal: {
      get: function () {
        return this.displayMobilePopup;
      },
      set: function (newVal) {
        this.$store.dispatch("setDisplayMobilePopup", newVal);
      },
    },
    showMagicLinkWarningModal: {
      get: function () {
        return this.magicLinkTokenExpirationWarning;
      },
      set: function (newVal) {
        this.$store.dispatch("setMagicLinkTokenExpirationWarning", newVal);
      },
    },
    displayNavigationDrawer() {
      if (this.magicLinkToken) return false;
      return !this.routesWithoutNavBar.includes(this.$route.name);
    },
    displayNavigationBar() {
      return !this.routesWithoutNavBar.includes(this.$route.name);
    },
    showLogin() {
      return (
        this.whitelabelReady &&
        this.featureFlagsResolved &&
        !this.$auth.loading &&
        (this.$route.name == "login" ||
          this.$route.name == "spirit-login" ||
          this.$route.name == "generic-3pa-login")
      );
    },
    showMagicLinkLogin() {
      // Flags decide the gradient first. An unresolved feature-performx-logo
      // reads as Whistle and flashes before PerformX on a magic-link load.
      return this.whitelabelReady && this.featureFlagsResolved && this.$route.name == "magic-link";
    },
    showCardActivationSuccess() {
      if (this.$route.name == "walletactivationsuccess") {
        return true;
      }
      return false;
    },
    showCardPINSuccess() {
      if (this.$route.name == "walletpinsuccess") {
        return true;
      }
      return false;
    },
    showHostedVirtualCard() {
      if (this.$route.name == "marqetavirtualcardhosted") {
        return true;
      }
      return false;
    },
    showHostedActivateCard() {
      if (this.$route.name == "marqetaactivatecardhosted") {
        console.log("activate card");
        return true;
      }
      return false;
    },
    showHostedCardPIN() {
      if (this.$route.name == "marqetacreatepinhosted") {
        console.log("Create PIN for card");
        return true;
      }
      return false;
    },
    showClientBlacklist() {
      return (
        this.$auth.isAuthenticated &&
        this.userProfile &&
        this.userProfile.Client &&
        this.userProfile.Client.onboardingStatus == "BLACKLIST"
      );
    },
    /** ADO #189684: the Active domains this user's own client owns, lowercased. */
    clientWhitelabelDomains() {
      const rows =
        (this.userProfile &&
          this.userProfile.Client &&
          this.userProfile.Client.ClientWhitelabelConfigurations) ||
        [];
      return rows.map((row) => (row.domain || "").toLowerCase()).filter(Boolean);
    },
    /**
     * ADO #189684: Auth0 has settled and left us with a live session.
     *
     * userProfile is persisted, so a reload rehydrates the previous session's
     * profile before Auth0 has had the chance to reject it. Without this, an
     * expired session is judged on that profile and the modal opens over the
     * login screen it is on its way to.
     *
     * Magic links are covered: that flow decodes its own token into
     * $auth.user, so isAuthenticated comes back true for it without an Auth0
     * round trip. Waiting on loading as well keeps a genuine sign-in from
     * being judged on the previous user's profile while setUser replaces it.
     */
    sessionReady() {
      return Boolean(this.$auth && !this.$auth.loading && this.$auth.isAuthenticated);
    },
    /** ADO #189684: the signed-in user's client does not own this host. */
    hostDenied() {
      // isDefault is only trustworthy once the whitelabel lookup has settled,
      // and meaningless if it failed — which also raises whitelabelReady and
      // routes to AppErrorScreen, so judging here would stack a modal on it.
      if (!this.whitelabelReady || this.failedWhitelabel) return false;
      if (!this.sessionReady) return false;
      if (!this.userProfile || !this.userProfile.Client) return false;

      const host = window.location.hostname.toLowerCase();
      const owned = this.clientWhitelabelDomains;
      // A client with its own site belongs only there. A client with none
      // belongs only on the platform hosts, which are the clientId=0 rows.
      return owned.length ? !owned.includes(host) : !this.$store.state.whitelabel.isDefault;
    },
    /**
     * Their own site when their client has one. A client with none belongs on
     * the platform, which the SPA has no way to name, so the modal drops the
     * redirect offer and only offers a way out.
     */
    hostRedirectDomain() {
      return this.clientWhitelabelDomains[0] || null;
    },
    showPaymentsActivation() {
      return (
        this.userProfile &&
        this.userProfile.firstLogin &&
        this.userProfile.Client &&
        this.userProfile.Client.onboardingLanguage &&
        this.userProfile.Client.onboardingLanguage.toLowerCase().includes("payment")
      );
      // return true;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
  },
  watch: {
    "$auth.loading": function (newVal) {
      /**
       * We add a watcher for auth0 loading because for whatever reason
       * if you use Safari, authGuard would get loaded by the router-link component a split second before the watcher component for auth0.loading
       * So my solution was to sleep so the watcher in authGuard would run first.
       */
      if (!newVal)
        setTimeout(() => {
          // Do not clear App loading until Tier 1 (whitelabel) has settled —
          // cached-profile auth can flip $auth.loading=false first.
          if (!this.whitelabelReady) return;
          this.loading = false;
          this.showLoginError = false;
        }, 10);

      // If we're done loading, no failed login, and the user isn't on login-error, then we wipe the path
      if (!this.failedLogin && this.$route.name == "login-error") this.$router.push("/");
    },
    // If auth finished before Tier 1, clear App loading once whitelabel settles.
    whitelabelReady: function (ready) {
      if (ready && this.$auth && !this.$auth.loading) {
        this.loading = false;
        this.showLoginError = false;
      }
    },
    "$auth.errorType": function (newVal) {
      console.log("New value for error type! ", newVal);
      this.showLoginError = true;
    },
  },
};
</script>

<style>
@font-face {
  font-family: "Avenir";
  src: local("Avenir"), url(./fonts/AvenirLTStd-Roman.otf) format("truetype");
}
@font-face {
  font-family: "Avenir Black";
  src: local("Avenir-Black"), url(./fonts/AvenirLTStd-Black.otf) format("truetype");
  font-weight: 900;
}
#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: black;
  background-color: var(--v-lightGrey-base);
}
/* Two halves of one guarantee: the logged-in page is grey from the top of the
   window to the bottom of the document.
   - The shell covers the window and is grey itself, so a short page can't
     reveal the white .v-application background behind it.
   - Only the root element's background reaches the page canvas, so grey on the
     shell still stops at the shell's box. Painting html covers whatever the
     document shows past it when a page overflows.
   Both are scoped to the logged-in shell; login, signup, and activation sit
   outside it and keep the default canvas for their own gradients. */
html:has(.authenticated-shell) {
  background-color: var(--v-lightGrey-base);
}
.authenticated-shell {
  min-height: 100vh;
  background-color: var(--v-lightGrey-base);
}
/* The banner is its own row above the page, so main-content-* padding no
   longer wraps it and the banner's own margin reads as page background. */
.page-slot {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.page-slot__body {
  flex: 1 1 auto;
  min-height: 0;
}
/* The banner's mb-4 is the gap; drop the page's top padding so the two don't
   stack. Two selectors so this outranks .main-content-top wherever that sits. */
.page-slot > .page-slot__body--below-banner {
  padding-top: 0;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
/* .v-application--wrap {
  margin-bottom: -89px;
} */
.gradient-background {
  background: linear-gradient(45deg, rgba(161, 215, 110, 1) 1%, rgba(10, 172, 235, 1) 100%), white;
  background-size: 100% 100%, 100%;
  background-repeat: no-repeat;
  background-position: bottom, top;
  width: 100%;
  height: 100%;
}
.background-gradient {
  background-color: var(--v-systemPrimary-base);
  background-image: radial-gradient(
      ellipse 105% 190% at 30% 109%,
      rgba(166, 34, 122, 0.38) 0%,
      transparent 58%
    ),
    radial-gradient(ellipse 130% 100% at 8% 15%, rgba(64, 211, 211, 0.29) 0%, transparent 80%),
    radial-gradient(ellipse 90% 140% at 108% 35%, rgba(64, 211, 211, 0.16) 0%, transparent 40%);
  background-attachment: fixed;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}
.white-background {
  background-color: white;
}
.primary-background {
  background-color: var(--v-primary-base);
}
.brand-background {
  background-color: var(--v-brand-base);
}
.brand-orange-background {
  background-color: var(--v-brandOrange-base);
}
.light-grey-background {
  background-color: var(--v-lightGrey-base);
}
.medium-grey-background {
  background-color: var(--v-mediumGrey-base);
}
.grey-background {
  background-color: var(--v-grey-base);
}
.brand-green-background {
  background-color: var(--v-brandGreen-base);
}
.brand-light-green-background {
  background-color: var(--v-brandLightGreen-base);
}
.brand-dark-green-background {
  background-color: var(--v-brandDarkGreen-base);
}
.brand-cyan-background {
  background-color: var(--v-brandCyan-base);
}
.brand-orange-background {
  background-color: var(--v-brandOrange-base);
}
.accent-background {
  background-color: var(--v-accent-base);
}
.accent-dark-background {
  background-color: var(--v-accentDark-base);
}
.full-height {
  height: 100%;
}
.full-width {
  width: 100%;
}
.full-screen-width {
  width: 100vw;
}
.full-screen-height {
  min-height: 100vh;
}
.main-content-top {
  padding-top: 10px;
  padding-bottom: 10px;
}
.main-content-sides {
  padding-left: 10px;
  padding-right: 10px;
}
/* .v-main__wrap { */
.pad {
  padding: 10px;
}
.align-text-left {
  text-align: left;
}
.align-text-right {
  text-align: right;
}
.align-text-center {
  text-align: center;
}
.hidden {
  visibility: hidden;
}
.cursor-default {
  cursor: default;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-grab {
  cursor: grab;
}
.cursor-wait {
  cursor: wait;
}
.selectable-blue-text {
  color: #3a6ed5;
}
.v-data-table-header {
  background-color: var(--v-lightGrey-base);
}

.v-dialog--fullscreen .max-height.v-card.v-sheet {
  overflow: scroll;
}

.dialog-header {
  background-color: var(--v-brandCyan-base);
  color: white;
  /* padding-bottom: 5%; */
  height: 70px;
}
.word-break {
  word-break: break-word;
}
.full-height {
  height: 100%;
}
.full-height.background-gradient {
  height: auto;
  min-height: 100%;
  min-height: 100vh;
}
.full-width {
  width: 100%;
}
.circle-border-radius {
  border-radius: 100%;
}

.mq-notification {
  z-index: 999 !important;
  margin-left: 75px !important;
}

/* Used for html timestamp selector. This will match Vue styling */
.timestamp-input {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  line-height: 20px;
  padding: 8px 12px;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  margin: 16px 0px;
  min-width: 235px;
  /* color: rgba(0, 0, 0, 0.38); */
}

/* .v-dialog__content--active {
  position: absolute;
  min-height: 100%;
}

.v-dialog--fullscreen {
  position: absolute;
  overflow-y: visible;
} */

/* Used to clip the text with ellipsis */
.text-overflow-clip {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

/* Used to wrap text to the next line */
.text-overflow-wrap {
  overflow-wrap: break-word;
  white-space: initial;
}

.whistle-gradient {
  background: rgb(162, 215, 109);
  background: linear-gradient(0deg, rgba(162, 215, 109, 1) 0%, #09acec);
}

.whistle-gradient-45 {
  background: linear-gradient(45deg, rgba(161, 215, 110, 1) 1%, rgba(10, 172, 235, 1) 100%), white;
  background-size: 100% 100%, 100%;
  background-repeat: no-repeat;
  background-position: bottom, top;
}

.ql-container {
  word-break: break-word;
}

.slide-group-button {
  padding: 5px 5px;
  border-radius: 7px;
  width: 300px;
  background-color: var(--v-lightGrey-base);
}

button.auth-type.v-btn .v-btn__content {
  justify-content: start !important;
}

.vjs-custom-skin .video-js {
  background: #ffffff !important;
}
.white-table-headers {
  /* border: solid 1px;
  border-color: lightgray; */
  border-radius: 20px;
  /* border-radius: 0px; */
}
.white-table-headers .v-data-table__wrapper {
  border-radius: 20px 0 0 0;
  padding-top: 10px;
}
.white-table-headers .v-data-table-header {
  background-color: white;
}

/* Used by the popup for recognitions */
.recognition-popup-dialog {
  height: 100%;
  overflow: hidden;
}

/* Styling for box with preset in account management and recognition wizard */
.preset-selector-box {
  padding: 16px 16px 16px 16px;
  display: flex;
  justify-content: start;
  align-items: center;
  border: 1px solid #d9d9d9 !important;
  min-width: 100%;
}
.preset-selector-box:hover {
  background-color: #eeeeee;
}
.preset-selector-emoji {
  font-size: 36px;
  margin-right: 16px;
}

.box-border-global {
  border: solid 1px;
  border-color: lightgray;
  border-radius: 0;
}
.ellipsis-fill {
  flex-shrink: 0 !important;
  flex-grow: 1 !important;
  border-bottom: dotted black 1px;
  transform: translateY(-7px);
}

.text-xs {
  font-size: x-small;
}
.text-sm {
  font-size: small;
}
.text-md {
  font-size: medium;
}
.text-lg {
  font-size: large;
}
.text-xl {
  font-size: x-large;
}

/* Dashboard widget text sizes, matching the leaderboard scale in LeaderboardCreator.vue.
   ProgramDashboardV1.vue overrides these with its em-based sizes under .pgm-dashboard-v1. */
.pgm-dashboard-text-xs {
  font-size: 0.75rem;
}
.pgm-dashboard-text-sm {
  font-size: 0.875rem;
}
.pgm-dashboard-text-md {
  font-size: 1rem;
}
.pgm-dashboard-text-lg {
  font-size: 1.25rem;
}
.pgm-dashboard-text-xl {
  font-size: 1.5rem;
}

.clickthrough {
  pointer-events: none;
}
.clickthrough > * {
  pointer-events: all;
}
.v-badge.nudge-up * {
  transform: translate(-6px, -6px);
}

.pointer-events-none {
  pointer-events: none;
}
.user-select-none {
  user-select: none;
}

/* Audience chip for all wizards */
/* Changes the close icon's color for selected audience chips */
.wizard-audience-chip >>> .v-chip__close {
  color: lightgray;
}
/* Thickens the border on audience chips */
.wizard-audience-chip.v-chip.v-chip--outlined {
  border-width: 2px !important;
}

/* outline used for the main content card */
.wizard-main-card {
  border: solid 1px;
  border-color: lightgray;
  min-height: calc(100vh - 120px);
}

/* Divider because v-divider doesn't appear when flex-direction is column */
.divider-global {
  border: 1px solid lightgray;
  border-bottom: 0;
}
</style>
